import React from 'react';

/**
 * ContentSectionEditor - Sub-form for adding, removing, and reordering Content_Sections.
 *
 * Props:
 * - content: array of { heading, body, order } objects
 * - onChange(content): callback when content sections change
 *
 * Functionality:
 * - Add new section button (adds at end)
 * - Remove section button per section
 * - Move up/down buttons per section
 * - When order changes, recalculates order fields to be contiguous 0-based sequence
 * - Heading and body are editable
 */
function ContentSectionEditor({ content = [], onChange }) {
  /**
   * Recalculate order fields to maintain a contiguous 0-based sequence.
   */
  function reindex(sections) {
    return sections.map((section, index) => ({
      ...section,
      order: index,
    }));
  }

  function handleAddSection() {
    const updated = [
      ...content,
      { heading: '', body: '', order: content.length },
    ];
    onChange(reindex(updated));
  }

  function handleRemoveSection(index) {
    const updated = content.filter((_, i) => i !== index);
    onChange(reindex(updated));
  }

  function handleMoveUp(index) {
    if (index === 0) return;
    const updated = [...content];
    const temp = updated[index - 1];
    updated[index - 1] = updated[index];
    updated[index] = temp;
    onChange(reindex(updated));
  }

  function handleMoveDown(index) {
    if (index >= content.length - 1) return;
    const updated = [...content];
    const temp = updated[index + 1];
    updated[index + 1] = updated[index];
    updated[index] = temp;
    onChange(reindex(updated));
  }

  function handleHeadingChange(index, value) {
    const updated = content.map((section, i) =>
      i === index ? { ...section, heading: value } : section
    );
    onChange(updated);
  }

  function handleBodyChange(index, value) {
    const updated = content.map((section, i) =>
      i === index ? { ...section, body: value } : section
    );
    onChange(updated);
  }

  return (
    <div className="content-section-editor">
      {content.length === 0 && (
        <p className="content-section-empty">No content sections yet.</p>
      )}

      {content.map((section, index) => (
        <div key={index} className="content-section-item" data-testid={`content-section-${index}`}>
          <div className="content-section-controls">
            <span className="content-section-order">#{section.order + 1}</span>
            <button
              type="button"
              className="content-section-move-up-btn"
              onClick={() => handleMoveUp(index)}
              disabled={index === 0}
              aria-label={`Move section ${index + 1} up`}
            >
              ↑
            </button>
            <button
              type="button"
              className="content-section-move-down-btn"
              onClick={() => handleMoveDown(index)}
              disabled={index >= content.length - 1}
              aria-label={`Move section ${index + 1} down`}
            >
              ↓
            </button>
            <button
              type="button"
              className="content-section-remove-btn"
              onClick={() => handleRemoveSection(index)}
              aria-label={`Remove section ${index + 1}`}
            >
              ✕
            </button>
          </div>

          <div className="content-section-fields">
            <div className="content-section-field">
              <label htmlFor={`content-heading-${index}`}>Heading</label>
              <input
                id={`content-heading-${index}`}
                type="text"
                value={section.heading}
                onChange={(e) => handleHeadingChange(index, e.target.value)}
                placeholder="Section heading"
              />
            </div>

            <div className="content-section-field">
              <label htmlFor={`content-body-${index}`}>Body (Markdown)</label>
              <textarea
                id={`content-body-${index}`}
                value={section.body}
                onChange={(e) => handleBodyChange(index, e.target.value)}
                placeholder="Section content (supports markdown)"
                rows={4}
              />
            </div>
          </div>
        </div>
      ))}

      <button
        type="button"
        className="content-section-add-btn"
        onClick={handleAddSection}
      >
        + Add Content Section
      </button>
    </div>
  );
}

export default ContentSectionEditor;
